import React from 'react';
import { Wallet, Clock, TrendingUp, CircleCheck, CalendarClock } from 'lucide-react';

function formatNextPayout(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    // e.g. "Mon 24 Feb · 04:00 UTC"
    const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    return `${day} · ${time} UTC`;
  } catch { return null; }
}

function daysUntil(iso) {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'today';
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
}

export function BalanceCards({ earnings }) {
  const nextPayoutLabel = formatNextPayout(earnings.next_payout_at);
  const nextPayoutRelative = daysUntil(earnings.next_payout_at);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {/* Available for Payout */}
      <div className="card-surface p-6 border-l-4 border-l-emerald-500" data-testid="available-balance-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-emerald-600" />
          </div>
          <span className="text-sm text-stone-500">Available for Payout</span>
        </div>
        <p className="text-3xl font-semibold text-[#1A1A1A]" data-testid="available-amount">
          £{earnings.available_for_payout.toFixed(2)}
        </p>
        {earnings.meets_payout_threshold ? (
          <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
            <CircleCheck className="h-3.5 w-3.5" />
            Will be paid out {nextPayoutRelative || 'next Monday'}
          </p>
        ) : (
          <p className="text-xs text-stone-400 mt-2">
            £{(earnings.payout_threshold - earnings.available_for_payout).toFixed(2)} more to reach the £{earnings.payout_threshold.toFixed(0)} threshold
          </p>
        )}
      </div>

      {/* Pending */}
      <div className="card-surface p-6 border-l-4 border-l-amber-400" data-testid="pending-balance-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
            <Clock className="h-5 w-5 text-amber-500" />
          </div>
          <span className="text-sm text-stone-500">Pending</span>
        </div>
        <p className="text-3xl font-semibold text-[#1A1A1A]" data-testid="pending-amount">
          £{earnings.pending_balance.toFixed(2)}
        </p>
        <p className="text-xs text-stone-400 mt-2">
          Clears {earnings.payout_delay_hours || 48}h after service completion
        </p>
      </div>

      {/* Next Payout */}
      <div className="card-surface p-6 border-l-4 border-l-[#D4AF37]" data-testid="next-payout-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center">
            <CalendarClock className="h-5 w-5 text-[#D4AF37]" />
          </div>
          <span className="text-sm text-stone-500">Next Payout</span>
        </div>
        <p className="text-xl font-semibold text-[#1A1A1A]" data-testid="next-payout-date">
          {nextPayoutLabel || ','}
        </p>
        <p className="text-xs text-stone-400 mt-2">
          Weekly · Minimum £{earnings.payout_threshold.toFixed(0)} · Automatic
        </p>
      </div>

      {/* Total Earned */}
      <div className="card-surface p-6 border-l-4 border-l-stone-300" data-testid="total-earned-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-stone-50 flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-stone-500" />
          </div>
          <span className="text-sm text-stone-500">Total Payout</span>
        </div>
        <p className="text-3xl font-semibold text-[#1A1A1A]" data-testid="total-earned-amount">
          £{earnings.total_earned.toFixed(2)}
        </p>
        <p className="text-xs text-stone-400 mt-2">
          £{earnings.total_paid_out.toFixed(2)} paid out so far
        </p>
      </div>
    </div>
  );
}
